import { Menu } from 'antd';
import type { MenuProps } from 'antd';
import { I18n } from '@aws-amplify/core';
import { useHistory } from 'react-router';
import * as Icon from '@ant-design/icons';
import { useLocation } from 'react-router-dom';

import Can from '@/components/Can';
import { MenuItem, ItemParams } from './types';
import { PersonalisedReportSVG } from '@/components/Icons/PersonalisedReportSVG';
import { PsychosocialReportSurveySVG } from '@/components/Icons/PsychosocialReportSurveySVG';
import { PsychosocialReportSVG } from '@/assets/svg/PsychosocialReportSVG';
import { RotationIcon } from '@/assets/icons/Rotation';
import { MenuLabel } from './MenuLabel';

export function MenuOptions() {
	const history = useHistory();
	const location = useLocation();
	const selectKey = location.pathname;

	function getItem({
		canAccess = false,
		disabled = false,
		key = null,
		children,
		label,
		icon,
		type
	}: ItemParams): MenuItem {
		if (!canAccess) {
			return null;
		}

		return {
			key,
			icon,
			children,
			label,
			type,
			disabled
		} as MenuItem;
	}

	const items: MenuItem[] = [
		getItem({
			label: <MenuLabel label="Dashboard" />,
			key: '/',
			icon: <Icon.BarChartOutlined />,
			canAccess: Can('access', 'ergonomic')
		}),
		getItem({
			label: <MenuLabel label="Upload" />,
			key: '/upload',
			icon: <Icon.CloudUploadOutlined />,
			canAccess: Can('access', 'ergonomic')
		}),
		getItem({
			label: <MenuLabel label="Files" />,
			key: '/files',
			icon: <Icon.FolderOpenOutlined />,
			canAccess: Can('access', 'ergonomic')
		}),
		getItem({
			label: <MenuLabel label="Reports" />,
			key: '/reporting',
			icon: <Icon.FilePdfOutlined />,
			canAccess: Can('access', 'ergonomic')
		}),
		getItem({
			label: <MenuLabel label="Multitasks and rotation" />,
			key: '/rotation',
			icon: <RotationIcon width={15} height={15} />,
			canAccess: Can('access', 'ergonomic')
		}),
		// getItem({
		// 	key: '/ergonomic-work-analysis',
		// 	label: I18n.get('EWA'),
		// 	icon: <Icon.FileProtectOutlined />
		// }),
		getItem({
			key: '/super-pea',
			label: I18n.get('PEA Aggregator'),
			icon: <Icon.FileProtectOutlined />,
			canAccess: Can('access', 'ergonomic')
		}),
		getItem({
			label: <MenuLabel label="Action plans" />,
			key: '/action-plans',
			icon: <Icon.ReconciliationOutlined />,
			canAccess: Can('access', 'ergonomic')
		}),
		getItem({
			key: null,
			label: <MenuLabel label="Psychosocial" />,
			icon: <PsychosocialReportSVG />,
			canAccess: Can('access', 'psychosocial'),
			children: [
				getItem({
					label: I18n.get('Survey management'),
					key: '/psychosocial-report/survey-management',
					icon: <PsychosocialReportSurveySVG width={15} height={15} margin="0 0.5rem 0 0" />,
					canAccess: Can('access', 'psychosocial')
				}),
				getItem({
					label: I18n.get('Employees management'),
					key: '/psychosocial-report/employees',
					icon: <Icon.TeamOutlined />,
					canAccess: Can('access', 'psychosocial')
				})
			]
		}),
		getItem({
			key: null,
			label: <MenuLabel label="Settings" />,
			icon: <Icon.InfoCircleOutlined />,
			canAccess: Can('access', 'ergonomic') || Can('access', 'psychosocial'),
			children: [
				getItem({
					label: I18n.get('My account'),
					key: '/settings/account',
					icon: <Icon.InfoCircleOutlined />,
					canAccess: Can('access', 'ergonomic') || Can('access', 'psychosocial')
				}),
				getItem({
					label: I18n.get('Preferences'),
					key: '/settings/preferences',
					icon: <Icon.ControlOutlined />,
					disabled: !Can('settings', 'preferences'),
					canAccess: Can('access', 'ergonomic')
				}),
				getItem({
					label: I18n.get('Manage users'),
					key: '/users',
					icon: <Icon.TeamOutlined />,
					disabled: !Can('settings', 'management'),
					canAccess: Can('access', 'ergonomic') || Can('access', 'psychosocial')
				}),
				getItem({
					label: I18n.get('Manage companies'),
					key: '/settings/companies',
					icon: <Icon.PartitionOutlined />,
					disabled: !Can('settings', 'management'),
					canAccess: Can('access', 'ergonomic') || Can('access', 'psychosocial')
				}),
				getItem({
					label: I18n.get('Personalised reports'),
					key: '/settings/personalised-reports',
					icon: (
						<PersonalisedReportSVG
							width={15}
							height={15}
							margin="0 0.5rem 0 0"
							disabled={!Can('settings', 'personalise_report')}
						/>
					),
					disabled: !Can('settings', 'personalise_report'),
					canAccess: Can('access', 'ergonomic')
				})
			]
		})
	];

	const handleOnClick: MenuProps['onClick'] = ({ key }): void => {
		history.push(key);
	};

	return (
		<Menu
			theme="light"
			mode="inline"
			items={items}
			onClick={handleOnClick}
			selectedKeys={[selectKey]}
			defaultSelectedKeys={[selectKey]}
		/>
	);
}
